import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import moment from "moment";
import Joi from "joi-browser";
import Input from "./Input";
import Select from "./Select";
import TextArea from "./TextArea";
import FieldDisplay from "./FieldDisplay";
import RadioButton from "./RadioButton";
import diagnosisOptionSets from "../../options/diagnosis.json";
import diagnosisDetailOptionSets from "../../options/diagnosisdetail.json";
import {
  getUsers,
  searchDSM5,
  searchICD10,
  getClientDiagnosisDetails,
} from ".././services/soar";
import {
  createEntity,
  getFieldDate,
  getFieldName,
  getFieldText,
  updateEntity,
} from "../../scripts/helperScripts";

function DiagnosisModal({
  isOpen,
  hideModal,
  nextModal,
  isLastModal,
  clientId,
  clientProgram,
  initialData,
  updateDiagnosis,
}) {
  const clientProgramAdmitDate = new Date(
    clientProgram && getFieldDate(clientProgram.ccx_admitdate)
  );
  const [dx, setDx] = useState({});
  const [diagnosisOptions, setDiagnosisOptions] = useState([]);
  const [clientDiagnoses, setClientDiagnoses] = useState([]);
  const [providers, setProviders] = useState([]);
  const [providerSearchQuery, setProviderSearchQuery] = useState("");
  const [diagnosisSearchQuery, setDiagnosisSearchQuery] = useState("");
  const [errors, setErrors] = useState({});
  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  //Validation Schema
  const joiSchema = {
    ccx_clientdiagnosis: Joi.string().allow(null, ""),
    ccx_clientdiagnosisdetailid: Joi.string().allow(null, ""),
    dxccx_diagnosistype: Joi.string().required().label("Diagnosis Set"),
    diagnosis: Joi.string().required().label("Diagnosis"),
    dxccx_sourceofdata: Joi.string().required().label("Source of Data"),
    dxccx_diagnosingprovider: Joi.string()
      .label("Diagnosing Provider")
      .when("dxccx_sourceofdata", {
        is: "803080000",
        then: Joi.string().required(),
      }),
    dxccx_reportedby: Joi.string()
      .allow("")
      .label("Reported By")
      .when("dxccx_sourceofdata", {
        is: "803080001",
        then: Joi.string().required().label("Reported By"),
      }),
    dxccx_providername: Joi.string()
      .allow("")
      .label("Provider Name")
      .when("dxccx_reportedby", {
        is: "803080001",
        then: Joi.string().required(),
      })
      .when("dxccx_reportedby", {
        is: "803080002",
        then: Joi.string().required(),
      }),
    ccx_rank: Joi.number().min(0).max(100).required().label("Rank"),
    ccx_reasonforaction: Joi.string().allow("").label("Reason for Action"),
    ccx_diagnosistype: Joi.string().required().label("Diagnosis Type"),
    ccx_presentonadmissionos: Joi.string()
      .required()
      .label("Present on Admission"),
    sor_notes: Joi.string().allow("").label("Additional Specifiers"),
    dxccx_effdate: Joi.date()
      .required()
      .min(new Date(moment(clientProgramAdmitDate).subtract(1, "day").toDate()))
      .max(
        moment(clientProgramAdmitDate).isAfter(moment())
          ? moment(clientProgramAdmitDate).toDate()
          : "now"
      )
      .label("Effective Date")
      .error((errors) => {
        errors.forEach((error) => {
          console.log("Effective Date error: ", error);
          switch (error.type) {
            case "date.max":
              error.message = `Effective Date cannot be future dated beyond ${moment(
                error.context.limit
              ).format("L")}`;
              break;
            case "date.min":
              error.message = `Effective Date cannot be before ${moment(
                error.context.limit
              )
                .add(1, "d")
                .format("L")}`;
              break;
            case "date.base":
              error.message = `Effective Date is required`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      }),
    dxccx_expdate: Joi.alternatives([
      Joi.date()
        .min(new Date(dx.dxccx_effdate))
        .label("Expiration Date")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.min":
                error.message = `Expiration Date needs to be after Diagnosis Effective Date of ${moment
                  .utc(error.context.limit)
                  .format("L")}`;
                break;
              case "date.max":
                error.message = `Expiration Date cannot be future dated beyond Diagnosis Effective Date of ${moment
                  .utc(error.context.limit)
                  .format("L")}`;
                break;
              default:
                return error.message;
            }
          });
          return errors;
        }),
      Joi.string().valid(""),
    ]),
    ccx_expdate: Joi.alternatives([
      Joi.date()
        .min(new Date(dx.dxccx_effdate))
        .label("Expiration Date")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.min":
                error.message = `Expiration Date needs to be after Diagnosis Effective Date of ${error.context.limit}`;
                break;
              case "date.max":
                error.message = `Expiration Date cannot be future dated`;
                break;
              default:
                return error.message;
            }
          });
          return errors;
        }),
      Joi.string().valid(""),
    ]),
  };

  useEffect(() => {
    if (!safyInfo) navigate("/");
  });

  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  useEffect(() => {
    if (initialData) setDx(initialData);
    else
      setDx({
        dxccx_diagnosistype: "803080002",
        dxccx_sourceofdata: "803080000",
        dxccx_diagnosingprovider: `{${safyInfo.user.safyInfo.SOARUserId}}`,
      });
  }, [initialData]);

  useEffect(() => {
    filterUsers();
  }, [providers]);

  useEffect(() => {
    if (isOpen) {
      getUsers().then((response) => {
        if (response && isMountedRef.current) {
          setProviders(response);
        }
      });

      getClientDiagnosisDetails(clientId).then((response) => {
        if (response && isMountedRef.current) {
          setClientDiagnoses(response);
        }
      });
    }
  }, [isOpen]);

  const filterUsers = () => {
    let filtered = providers;
    if (providerSearchQuery) {
      filtered = filtered.filter(
        (user) =>
          user.fullname &&
          user.fullname
            .toLowerCase()
            .includes(providerSearchQuery.toLowerCase())
      );
    }
    const options = filtered.map((user) => {
      return {
        _id: user.systemuserid,
        name: user.fullname,
      };
    });
    return options;
  };

  const handleChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input);
    if (errorMessage) {
      errs[input.name] = errorMessage;
    } else {
      delete errs[input.name];
    }
    const data = dx;
    data[input.name] = input.value;
    setDx(data);

    setErrors(errs);
  };

  const validate = () => {
    if (dx.ccx_clientdiagnosisdetailid) return null;
    const options = { abortEarly: false };
    const { error } = Joi.validate(dx, joiSchema, options);
    if (!error) return null;

    let validationErrors = {};
    for (let item of error.details)
      validationErrors[item.path[0]] = item.message;
    setErrors(validationErrors);
    return validationErrors;
  };

  const handleSave = () => {
    if (dx.ccx_clientdiagnosisid !== undefined) {
      if (dx.dxccx_expdate !== undefined && dx.dxccx_expdate !== "") {
        const dxObj = {
          ccx_expdate: {
            type: "datetime",
            value: new Date(dx.dxccx_expdate),
          },
        };

        const dxToastId = toast.loading("Diagnosis is being updated...", {
          type: toast.TYPE.INFO,
        });
        updateEntity(
          "ccx_clientdiagnosis",
          dx.ccx_clientdiagnosisid,
          JSON.stringify(dxObj),
          safyInfo
        )
          .then(() => {
            toast.update(dxToastId, {
              render: "Diagnosis has been updated.",
              type: toast.TYPE.SUCCESS,
              isLoading: false,
              closeOnClick: true,
              autoClose: 5000,
            });
            updateDiagnosis && updateDiagnosis(true);
          })
          .catch(() => {
            toast.update(dxToastId, {
              render: "An error occurred while updating the Diagnosis.",
              type: toast.TYPE.ERROR,
              isLoading: false,
              closeOnClick: true,
              autoClose: 5000,
            });
          });
      }

      if (dx.ccx_expdate !== undefined && dx.ccx_expdate !== "") {
        const dxDetailToastId = toast.loading(
          "Diagnosis Detail is being updated...",
          {
            type: toast.TYPE.INFO,
          }
        );
        const dxDetailObj = {
          ccx_expdate: {
            type: "datetime",
            value: new Date(dx.ccx_expdate),
          },
        };

        updateEntity(
          "ccx_clientdiagnosisdetail",
          dx.ccx_clientdiagnosisdetailid,
          JSON.stringify(dxDetailObj),
          safyInfo
        )
          .then(() => {
            toast.update(dxDetailToastId, {
              render: "Diagnosis Detail has been updated.",
              type: toast.TYPE.SUCCESS,
              isLoading: false,
              closeOnClick: true,
              autoClose: 5000,
            });
            updateDiagnosis && updateDiagnosis(true);
          })
          .catch(() => {
            toast.update(dxDetailToastId, {
              render: "An error occurred while updating the Diagnosis Detail.",
              type: toast.TYPE.ERROR,
              isLoading: false,
              closeOnClick: true,
              autoClose: 5000,
            });
          });
      }
    } else {
      const diagnosis = diagnosisOptions.filter(
        (option) => option._id === dx.diagnosis
      )[0];

      const dxObj = {
        ccx_contactid: {
          type: "entity",
          value: {
            entityName: "contact",
            entityId: clientId,
          },
        },
        ccx_name: {
          type: "string",
          value: diagnosis.dxName,
        },
        ccx_diagnosistype: {
          type: "optionset",
          value: parseInt(dx.dxccx_diagnosistype),
        },
        ccx_sourceofdata: {
          type: "optionset",
          value: parseInt(dx.dxccx_sourceofdata),
        },
        ccx_code: {
          type: "string",
          value: diagnosis.code,
        },
        ccx_effdate: {
          type: "datetime",
          value: new Date(dx.dxccx_effdate),
        },
        ccx_expdate: {
          type: "datetime",
          value: dx.dxccx_expdate ? new Date(dx.dxccx_expdate) : null,
        },
      };

      if (dx.dxccx_diagnosistype === "803080002")
        dxObj.ccx_icd10 = {
          type: "entity",
          value: {
            entityName: "ccx_icd10",
            entityId: dx.diagnosis,
          },
        };
      else if (dx.dxccx_diagnosistype === "803080003")
        dxObj.ccx_dsm5 = {
          type: "entity",
          value: {
            entityName: "ccx_dsm5",
            entityId: dx.diagnosis,
          },
        };

      if (dx.dxccx_sourceofdata === "803080000")
        dxObj.ccx_diagnosingprovider = {
          type: "entity",
          value: {
            entityName: "systemuser",
            entityId: dx.dxccx_diagnosingprovider,
          },
        };
      else if (dx.dxccx_sourceofdata === "803080001") {
        dxObj.ccx_reportedby = {
          type: "optionset",
          value: parseInt(dx.dxccx_reportedby),
        };

        if (
          dx.dxccx_reportedby === "803080001" ||
          dx.dxccx_reportedby === "803080002"
        )
          dxObj.ccx_providername = {
            type: "string",
            value: dx.dxccx_providername,
          };
      }

      const dxDetailObj = {
        ccx_contact: {
          type: "entity",
          value: {
            entityName: "contact",
            entityId: clientId,
          },
        },
        ccx_clientprogram: {
          type: "entity",
          value: {
            entityName: "ccx_client_program",
            entityId: clientProgram.ccx_client_programid,
          },
        },
        ccx_rank: {
          type: "number",
          value: dx.ccx_rank ? dx.ccx_rank : null,
        },
        ccx_reasonforaction: {
          type: "optionset",
          value: dx.ccx_reasonforaction
            ? parseInt(dx.ccx_reasonforaction)
            : null,
        },
        ccx_diagnosistype: {
          type: "optionset",
          value: dx.ccx_diagnosistype ? parseInt(dx.ccx_diagnosistype) : null,
        },
        ccx_presentonadmissionos: {
          type: "optionset",
          value: dx.ccx_presentonadmissionos
            ? parseInt(dx.ccx_presentonadmissionos)
            : null,
        },
        sor_notes: {
          type: "string",
          value: dx.sor_notes ? dx.sor_notes : null,
        },
        ccx_effdate: {
          type: "datetime",
          value: new Date(dx.dxccx_effdate),
        },
        ccx_expdate: {
          type: "datetime",
          value: dx.ccx_expdate ? new Date(dx.ccx_expdate) : null,
        },
      };
      const existingDiagnosis = clientDiagnoses.filter(
        (clientDiagnosis) =>
          clientDiagnosis.ccx_expdate === undefined &&
          (getFieldText(clientDiagnosis["dx.ccx_icd10"]) === dx.diagnosis ||
            getFieldText(clientDiagnosis["dx.ccx_dsm5"]) === dx.diagnosis)
      )[0];

      if (!existingDiagnosis) {
        const toastId = toast.loading(`Diagnosis is being created...`, {
          type: toast.TYPE.INFO,
        });

        createEntity(
          "ccx_clientdiagnosis",
          JSON.stringify(dxObj),
          safyInfo,
          getFieldName(clientProgram.ccx_company).split(" of ")[1]
        )
          .then((response) => {
            const newDx = response.resultset.result;

            dxDetailObj.ccx_clientdiagnosis = {
              type: "entity",
              value: {
                entityName: "ccx_clientdiagnosis",
                entityId: newDx.ccx_clientdiagnosisid,
              },
            };

            createEntity(
              "ccx_clientdiagnosisdetail",
              JSON.stringify(dxDetailObj),
              safyInfo,
              getFieldName(clientProgram.ccx_company).split(" of ")[1]
            )
              .then(() => {
                updateDiagnosis && updateDiagnosis(true);
                toast.update(toastId, {
                  render: "Diagnosis has been created.",
                  type: toast.TYPE.SUCCESS,
                  isLoading: false,
                  autoClose: 5000,
                  closeOnClick: true,
                });
              })
              .catch(() => {
                toast.update(toastId, {
                  render:
                    "An error occurred while creating the Diagnosis Detail.",
                  type: toast.TYPE.ERROR,
                  isLoading: false,
                  autoClose: 5000,
                  closeOnClick: true,
                });
              });
          })
          .catch(() => {
            toast.update(toastId, {
              render: "An error occurred while creating the Diagnosis.",
              type: toast.TYPE.ERROR,
              isLoading: false,
              autoClose: 5000,
              closeOnClick: true,
            });
          });
      } else {
        dxDetailObj.ccx_clientdiagnosis = {
          type: "entity",
          value: {
            entityName: "ccx_clientdiagnosis",
            entityId: getFieldText(existingDiagnosis.ccx_clientdiagnosis),
          },
        };

        const toastId = toast.loading("Diagnosis Detail is being created...", {
          type: toast.TYPE.INFO,
        });
        createEntity(
          "ccx_clientdiagnosisdetail",
          JSON.stringify(dxDetailObj),
          safyInfo,
          getFieldName(clientProgram.ccx_company).split(" of ")[1]
        )
          .then(() => {
            updateDiagnosis && updateDiagnosis(true);
            toast.update(toastId, {
              render: "Diagnosis Detail has been created.",
              type: toast.TYPE.SUCCESS,
              isLoading: false,
              autoClose: 5000,
              closeOnClick: true,
            });
          })
          .catch(() => {
            toast.update(toastId, {
              render: "An error occurred while creating the Diagnosis Detail.",
              type: toast.TYPE.ERROR,
              isLoading: false,
              autoClose: 5000,
              closeOnClick: true,
            });
          });
      }
    }
  };

  const validateProperty = ({ name, value }) => {
    const obj = { [name]: value };
    const schema = { [name]: joiSchema[name] };
    const { error } = Joi.validate(obj, schema);
    return error ? error.details[0].message : null;
  };

  const resetForm = () => {
    setDx({
      dxccx_diagnosistype: "803080002",
      dxccx_sourceofdata: "803080000",
      dxccx_diagnosingprovider: `{${safyInfo.user.safyInfo.SOARUserId}}`,
      diagnosis: "",
      dxccx_reportedby: "",
      dxccx_providername: "",
      ccx_rank: "",
      ccx_reasonforaction: "",
      ccx_diagnosistype: "",
      ccx_presentonadmissionos: "",
      dxccx_effdate: "",
      dxccx_expdate: "",
    });
    setDiagnosisSearchQuery("");
    setDiagnosisOptions([]);
    setErrors({});
  };

  const handleHideModal = () => {
    if (hideModal) {
      resetForm();
      hideModal();
    }
  };

  const handleSaveAndAdd = () => {
    handleSave();
    resetForm();
  };

  const handleDiagnosisTypeChange = (e) => {
    const obj = { ...dx };
    delete obj.diagnosis;
    setDx(obj);
    setDiagnosisOptions([]);
    handleChange(e);
  };

  const handleDiagnosisSearchChange = ({ currentTarget: input }) => {
    setDiagnosisSearchQuery(input.value);
  };

  const handleProviderSearchChange = ({ currentTarget: input }) => {
    setProviderSearchQuery(input.value);
  };

  const searchDiagnosis = () => {
    if (dx.dxccx_diagnosistype === undefined || dx.dxccx_diagnosistype === "") {
      const errs = { ...errors };
      errs.dxccx_diagnosistype = "Diagnosis Set is required";
      setErrors(errs);
    } else {
      if (dx.dxccx_diagnosistype === "803080002") {
        searchICD10(diagnosisSearchQuery).then((response) => {
          if (response && isMountedRef.current) {
            const options = response
              .map((icd10) => ({
                _id: icd10.ccx_icd10id,
                name: `${icd10.ccx_code}-${icd10.ccx_name}`,
                code: icd10.ccx_code,
                dxName: icd10.ccx_name,
              }))
              .sort((a, b) => {
                const nameA = a.code.toUpperCase();
                const nameB = b.code.toUpperCase();

                if (nameA < nameB) {
                  return -1;
                }

                if (nameA > nameB) {
                  return 1;
                }

                return 0;
              });
            setDiagnosisOptions(options);
          }
        });
      } else if (dx.dxccx_diagnosistype === "803080003") {
        searchDSM5(diagnosisSearchQuery).then((response) => {
          if (response && isMountedRef.current) {
            const options = response
              .map((dsm5) => ({
                _id: dsm5.ccx_dsm5id,
                name: `${dsm5.ccx_code}-${dsm5.ccx_name}`,
                code: dsm5.ccx_code,
                dxName: dsm5.ccx_name,
              }))
              .sort((a, b) => {
                const nameA = a.code.toUpperCase();
                const nameB = b.code.toUpperCase();

                if (nameA < nameB) {
                  return -1;
                }

                if (nameA > nameB) {
                  return 1;
                }

                return 0;
              });
            setDiagnosisOptions(options);
          }
        });
      }
    }
  };

  return (
    <>
      {isOpen ? (
        <Modal show={isOpen} animation={false}>
          {!dx.ccx_clientdiagnosisid && (
            <Modal.Header>
              <Modal.Title>Add Diagnosis</Modal.Title>
            </Modal.Header>
          )}
          <Modal.Body>
            <>
              {dx.ccx_clientdiagnosisid && (
                <>
                  <div className="h4">Diagnosis</div>
                  <div className="border border-success rounded p-2 mb-2">
                    <div
                      className="p-2 mt-4 mb-4 w-100 h-100"
                      style={{
                        backgroundColor: "white",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      <strong>
                        {dx.ccx_code}-{dx.clientDiagnosisName}
                      </strong>
                    </div>
                    <FieldDisplay
                      label="Diagnosis Set"
                      value={dx.dxccx_diagnosistype}
                    />
                    <FieldDisplay
                      label="Source of Data"
                      value={dx.dxccx_sourceofdata}
                    />
                    <FieldDisplay
                      label="Diagnosing Provider"
                      value={dx.dxccx_diagnosingprovider}
                    />
                    <FieldDisplay
                      className="mb-4"
                      label="Effective Date"
                      value={dx.dxccx_effdate}
                    />
                    <Input
                      name="dxccx_expdate"
                      type="date"
                      label="Diagnosis Expiration Date"
                      defaultValue={dx.dxccx_expdate ? dx.dxccx_expdate : ""}
                      onChange={handleChange}
                      error={errors.dxccx_expdate}
                      disabled={
                        clientDiagnoses.filter(
                          (clientDiagnosis) =>
                            clientDiagnosis.ccx_expdate === undefined &&
                            getFieldText(
                              clientDiagnosis.ccx_clientdiagnosis
                            ) === dx.ccx_clientdiagnosisid
                        ).length > 1
                      }
                    />
                  </div>
                  <div className="h4">Diagnosis Detail</div>
                  <div className="border border-success rounded p-2 mb-2">
                    <FieldDisplay
                      label="Client Program"
                      value={dx.clientprogramName}
                    />
                    {/* <FieldDisplay label="Diagnosis Type" value={dx.ccx_diagnosistype} /> */}
                    <FieldDisplay
                      className="mb-4"
                      label="Effective Date"
                      value={dx.ccx_effdate}
                    />
                    <Input
                      name="ccx_rank"
                      label="Rank"
                      defaultValue={dx.ccx_rank}
                      onChange={handleChange}
                      disabled={dx.ccx_clientdiagnosisid}
                      error={errors.ccx_rank}
                    />
                    <Select
                      name="ccx_reasonforaction"
                      label="Reason for Action"
                      options={diagnosisDetailOptionSets.ccx_reasonforaction}
                      value={dx.ccx_reasonforaction}
                      onChange={handleChange}
                      disabled={dx.ccx_clientdiagnosisid}
                      error={errors.ccx_reasonforaction}
                    />
                    <Select
                      name="ccx_diagnosistype"
                      label="Diagnosis Type"
                      options={diagnosisDetailOptionSets.ccx_diagnosistype}
                      value={dx.ccx_diagnosistype}
                      onChange={handleChange}
                      disabled={dx.ccx_clientdiagnosisid}
                      error={errors.ccx_diagnosistype}
                    />
                    <Select
                      name="ccx_presentonadmissionos"
                      label="Present on Admission"
                      options={
                        diagnosisDetailOptionSets.ccx_presentonadmissionos
                      }
                      value={dx.ccx_presentonadmissionos}
                      onChange={handleChange}
                      disabled={dx.ccx_clientdiagnosisid}
                      error={errors.ccx_presentonadmissionos}
                    />
                    <TextArea
                      name="sor_notes"
                      label="Additional Specifiers"
                      value={dx.sor_notes}
                      disabled={dx.ccx_clientdiagnosisid}
                    />
                    <Input
                      name="ccx_expdate"
                      type="date"
                      label="Expiration Date"
                      defaultValue={dx.ccx_expdate ? dx.ccx_expdate : ""}
                      onChange={handleChange}
                      error={errors.ccx_expdate}
                    />
                  </div>
                </>
              )}
              {!dx.ccx_clientdiagnosisid ? (
                <>
                  <RadioButton
                    name="dxccx_diagnosistype"
                    label="Diagnosis Set:"
                    value={dx.dxccx_diagnosistype}
                    options={diagnosisOptionSets.ccx_diagnosistype}
                    onChange={handleDiagnosisTypeChange}
                    error={errors.dxccx_diagnosistype}
                  />
                  <div className="container">
                    <div className="row">
                      <div className="col">
                        <Input
                          name="diagnosisSearch"
                          label="Search for Diagnosis"
                          placeholder="Search for Diagnosis..."
                          value={diagnosisSearchQuery}
                          onChange={handleDiagnosisSearchChange}
                        />
                      </div>
                      <div className="col-3">
                        <button
                          style={{ marginTop: "30px" }}
                          className="btn btn-primary"
                          onClick={searchDiagnosis}
                          disabled={diagnosisSearchQuery === ""}
                        >
                          Search
                        </button>
                      </div>
                    </div>
                  </div>
                  <Select
                    name="diagnosis"
                    label="Diagnosis"
                    value={dx.diagnosis}
                    options={diagnosisOptions}
                    onChange={handleChange}
                    error={errors.diagnosis}
                    disabled={diagnosisOptions.length === 0}
                  />
                  <TextArea
                    name="sor_notes"
                    label="Additional Specifiers"
                    onChange={handleChange}
                    error={errors.sor_notes}
                  />
                  <RadioButton
                    name="dxccx_sourceofdata"
                    label="Source of Data:"
                    defaultValue="803080000"
                    value={dx.dxccx_sourceofdata}
                    options={diagnosisOptionSets.ccx_sourceofdata}
                    onChange={handleChange}
                    error={errors.dxccx_sourceofdata}
                  />
                  {dx.dxccx_sourceofdata === "803080000" ? (
                    <>
                      <Input
                        name="providerSearch"
                        label="Search for Provider"
                        placeholder="Search for Provider"
                        value={providerSearchQuery}
                        onChange={handleProviderSearchChange}
                      />
                      <Select
                        name="dxccx_diagnosingprovider"
                        label="Diagnosing Provider"
                        value={dx.dxccx_diagnosingprovider}
                        options={filterUsers()}
                        onChange={handleChange}
                        error={errors.dxccx_diagnosingprovider}
                      />
                    </>
                  ) : null}
                  {dx.dxccx_sourceofdata === "803080001" ? (
                    <>
                      <Select
                        name="dxccx_reportedby"
                        label="Reported by"
                        value={dx.dxccx_reportedby}
                        options={diagnosisOptionSets.ccx_reportedby}
                        onChange={handleChange}
                        error={errors.dxccx_reportedby}
                      />
                      {(dx.dxccx_reportedby === "803080001" ||
                        dx.dxccx_reportedby === "803080002") && (
                          <Input
                            name="dxccx_providername"
                            label="Provider Name"
                            value={dx.dxccx_providername}
                            onChange={handleChange}
                            error={errors.dxccx_providername}
                          />
                        )}
                    </>
                  ) : null}
                  <Input
                    name="ccx_rank"
                    label="Rank"
                    defaultValue={dx.ccx_rank}
                    value={dx.ccx_rank}
                    onChange={handleChange}
                    error={errors.ccx_rank}
                  />
                  <Select
                    name="ccx_reasonforaction"
                    label="Reason for Action"
                    options={diagnosisDetailOptionSets.ccx_reasonforaction}
                    value={dx.ccx_reasonforaction}
                    onChange={handleChange}
                    error={errors.ccx_reasonforaction}
                  />
                  <Select
                    name="ccx_diagnosistype"
                    label="Diagnosis Type"
                    options={diagnosisDetailOptionSets.ccx_diagnosistype}
                    value={dx.ccx_diagnosistype}
                    onChange={handleChange}
                    error={errors.ccx_diagnosistype}
                  />
                  <Select
                    name="ccx_presentonadmissionos"
                    label="Present on Admission"
                    options={diagnosisDetailOptionSets.ccx_presentonadmissionos}
                    value={dx.ccx_presentonadmissionos}
                    onChange={handleChange}
                    error={errors.ccx_presentonadmissionos}
                  />
                  {clientProgram === undefined ? (
                    <div>Client Program is being created...</div>
                  ) : null}
                  <Input
                    name="dxccx_effdate"
                    type="date"
                    label="Effective Date"
                    defaultValue={dx.dxccx_effdate ? dx.dxccx_effdate : ""}
                    value={dx.dxccx_effdate}
                    onChange={handleChange}
                    error={errors.dxccx_effdate}
                    disabled={clientProgram === undefined}
                  />
                </>
              ) : null}
            </>
          </Modal.Body>
          <Modal.Footer>
            {nextModal === undefined && (
              <>
                <button className="btn btn-danger" onClick={handleHideModal}>
                  Cancel
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) {
                      handleSave();
                      handleHideModal();
                    }
                  }}
                >
                  Submit
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) {
                      handleSaveAndAdd();
                    }
                  }}
                >
                  Submit and Add New
                </button>
              </>
            )}
            {nextModal && (
              <>
                <button className="btn btn-primary" onClick={nextModal}>
                  Skip
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) {
                      handleSave();
                      resetForm();
                      nextModal();
                    }
                  }}
                >
                  Submit and {isLastModal ? "End" : "Next"}
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) {
                      handleSaveAndAdd();
                    }
                  }}
                >
                  Submit and Add New
                </button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default DiagnosisModal;
